<template>
  <div class="multiple-selection-table-component question-type-component">
    <div class="hint-text" v-if="$props.question.hint">{{ $props.question.hint.get() }}</div>
    <div class="multiple-selection-table" :style="{ '--row-headers-grid-count': rowHeadersGridCount }">

    <div :id="`table-header-row-${instance.uid}`" class="multiple-selection-table-row table-header-row">
      <div class="multiple-selection-table-cell"></div>
      <div class="multiple-selection-table-cell" v-if="hasRowHeaders"></div>
      <template v-for="(columnHeader) in props.question.columnHeaders">
        <template v-if="verifyColumnDisplay(columnHeader.get())">
          <div class="multiple-selection-table-cell">
            {{ columnHeader.get() }}
          </div>
        </template>
      </template>
    </div>

    <div class="multiple-selection-table-row" v-for="(row, j) in props.question.children">

      <div class="multiple-selection-table-cell row-label" v-html="row.children[0].question.get()" />

      <div class="multiple-selection-table-cell display-contents" v-if="hasRowHeaders">
        <div v-for="(rowHeader) in props.question.rowHeaders" class="split-row-cell split-row-label">
          {{ rowHeader.get() }}
        </div>
      </div>

      <template v-for="(child, i) in row.children">
        <div class="multiple-selection-table-cell display-contents" v-if="i">
          <template v-for="(option) in child.options">
            <div class="multiple-selection-table-cell-option split-row-cell" v-if="verifyColumnDisplay(getCurrentHeaderName(i), option)">
              <label :for="`root_${$props.question.path}_${option.value}`">{{ option.name.get() }}</label>
              <input type="radio"
                     v-model="model[j]"
                     :id="`root_${$props.question.path}_${option.value}`"
                     :value="option.value"
                     :name="`root_${$props.question.path}_${j}`"
                     @change="onChange"
                     @click="onClick(j, option.value)"
              />
              <div class="input-state-indicator">
                <svg viewBox="0 0 14 14">
                  <path class="ui-checkbox-checkmark-path" fill="none" d="M1.01,7.53l3.72,3.72l8.57-8.5"/>
                </svg>
              </div>
            </div>
          </template>
        </div>
      </template>

    </div>

  </div>
  </div>
</template>

<script lang="ts" setup>
import QualityMentoringFormQuestion from '../../models/QualityMentoringFormQuestion';
import { getCurrentInstance, inject, onBeforeUnmount, onMounted, ref } from 'vue';

const emit = defineEmits(['change']);

const props = defineProps({
  question: QualityMentoringFormQuestion,
  submission: Object
})

const getValueFromSubmission = () => {
  if(!props.submission['root'][props.question.path]) {
    return {};
  }
  const values = Object.keys(props.submission['root'][props.question.path]).map((a) => parseInt(a));
  return props.question.children.map((row) => {
    return row.options.map(option => {
      return values.includes(option.value) ? option.value : false;
    }).reduce((a, b) => a || b, false);
  });
}

const model = ref(getValueFromSubmission());
const input = ref();

const onChange = () => {
  props.submission['root'][props.question.path] = Object.values(model.value).reduce((value, row: any) => {
    value[row] = true;
    return value;
  }, {});
  emit('change');
}

const onClick = (key, value) => {
  if(model.value[key] == value) {
    model.value[key] = false;
    onChange();
  }
}

const hasRowHeaders = ref(props.question.rowHeaders.length > 0);

const rowHeadersGridCount = ref(props.question.rowHeaders.length + 1);

if(!props.submission['root']) {
  props.submission['root'] = {};
}

if(!props.submission['root'][props.question.path]) {
  props.submission['root'][props.question.path] = {};
}

if (Array.isArray(props.submission['root'][props.question.path]) && props.submission['root'][props.question.path].length === 0) {
  props.submission['root'][props.question.path] = {};
}

function clear() {
  if(props.submission['root']) {
    delete props.submission['root'][props.question.path];
  }
}

function verifyColumnDisplay(columnHeaderName, checkBoxOption = null) {
  const columnDisplayModifier = props.question.columnDisplay;
  if (!columnDisplayModifier) return true;
  const requiredQuestionAnswers = props.submission['root'][columnDisplayModifier.required_question]
  const columnsToDisplay = [];
  for (const [box, isChecked] of Object.entries(requiredQuestionAnswers)) {
    if (isChecked && columnDisplayModifier.values.hasOwnProperty(box)) {
      columnsToDisplay.push(columnDisplayModifier.values[box].get());
    }
  }
  const shouldDisplay = columnsToDisplay.includes(columnHeaderName);
  if (!shouldDisplay && checkBoxOption) {
    delete props.submission['root'][props.question.path][checkBoxOption.value]
  }
  return shouldDisplay;
}

function getCurrentHeaderName(columnIndex) {
  const headers = props.question.columnHeaders;
  return headers[columnIndex - 1].get();
}

const instance = getCurrentInstance();
const print = inject("print");

const stickyTopPx = getComputedStyle(document.documentElement).getPropertyValue('--tabs-height');
const stickyTop = parseInt(stickyTopPx.replace('px', ''));

const observer = new IntersectionObserver(
    ([e]) => {
      e.target.classList.toggle('sticky', e.intersectionRatio < 1 && e.target.getBoundingClientRect().top <= stickyTop + 20)
    },
    {
      rootMargin: `-${stickyTop + 1}px 0px 0px 0px`,
      threshold: 1
    }
) as IntersectionObserver;

onMounted(() => {
  if(!print) {
    const el = document.getElementById(`table-header-row-${instance.uid}`);
    if(el) {
      observer.observe(el);
    } else {
      console.error("observer element is null");
    }
  }
});

onBeforeUnmount(() => {
  observer.disconnect();
});

defineExpose({
  clear
})
</script>
